import { Fragment } from "react/jsx-runtime";
import classNames from "classnames/bind";
import { ProductCard } from "../ProductCard";
import { isInStock } from "../utils/isInStock";
import {
  ConfigProps,
  Product,
  ProductListLayoutProps,
} from "../../types/types";
import styles from "./Grid.module.scss";
import {
  CreditBanner,
  CreditBannerType,
} from "../../product-list-components/CreditBanner/CreditBanner";
import { YouveViewed } from "../../product-list-components/YouveViewed/YouveViewed";
import { useFeatureFlags } from "@~/flags/flags";
import {
  DisruptorBlock,
  DisruptorBlockDetails,
} from "@~/product-list-components/DisruptorBlock/DisruptorBlock";

const cx = classNames.bind(styles);

const getContainerTestId = (configProps: ConfigProps) => {
  if (configProps?.fullWidth) {
    return "grid-container-fullWidth";
  }
  if (configProps?.listView) {
    return "grid-container-list-view";
  }
  if (configProps?.largeCards) {
    return "grid-container-large-cards";
  }
  return "grid-container-small-cards";
};

export const Grid = ({
  configProps,
  config,
  products,
  resultsInfo,
}: ProductListLayoutProps) => {
  const flags = useFeatureFlags();

  const hideOutOfStocks = config["product-list-layout"]?.props?.hideOutOfStocks;

  const filteredProducts = products.filter((product) =>
    hideOutOfStocks ? isInStock(product) : true,
  );

  const displayCreditBanners = !!configProps?.creditBanner;

  const creditBannerDetails = getCreditBannerDetails(
    filteredProducts,
    resultsInfo.chunkSize,
  );

  const disruptorBlockDetails = getDisruptorBlockDetails(
    {
      esiPath: extractStringValue(flags.disruptorBlockEsiPath.value),
      id: extractStringValue(flags.disruptorBlockId.value),
      gridIndex: extractNumberValue(flags.disruptorBlockGridIndex.value),
      maxWidth: extractNumberValue(flags.disruptorBlockMaxWidth.value),
    },
    creditBannerDetails,
  );

  return (
    <section
      className={cx("grid-container", {
        "grid-container--fullWidth-small":
          configProps?.fullWidth && !configProps.largeCards,
        "grid-container--fullWidth-large":
          configProps?.fullWidth && configProps.largeCards,
        "grid-container--large-cards":
          configProps?.largeCards && !configProps.fullWidth,
        "grid-container--list-view": configProps?.listView,
        "grid-container--curated-categories": configProps?.curatedCategories,
      })}
      data-testid={getContainerTestId(configProps)}
    >
      {filteredProducts.map((product, ind) => {
        const hasCreditBanner =
          creditBannerDetails.indices.includes(ind) && displayCreditBanners;

        return (
          <Fragment
            key={
              product.advertising
                ? `${product.aliases.pimProductId}-sponsored`
                : product.aliases.pimProductId
            }
          >
            <ProductCard product={product} config={config} index={ind} />

            {shouldDisplayDisruptorBlock(
              ind,
              disruptorBlockDetails,
              filteredProducts.length,
            ) && (
              <DisruptorBlock disruptorBlockDetails={disruptorBlockDetails} />
            )}

            {hasCreditBanner && (
              <CreditBanner
                creditBannerType={creditBannerDetails.type}
                className={styles.fullWidth}
              />
            )}

            {!!configProps?.youveViewed && (
              <YouveViewed
                resultsInfo={resultsInfo}
                numProductsOnPageAbove={ind + 1}
              />
            )}
          </Fragment>
        );
      })}
    </section>
  );
};

const extractStringValue = (value: string | boolean | null) =>
  typeof value === "string" ? value : undefined;

const extractNumberValue = (value: string | boolean | null) =>
  typeof value === "string" ? parseInt(value, 10) : undefined;

const getDisruptorBlockDetails = (
  {
    id,
    gridIndex = 11,
    maxWidth = 4,
    esiPath,
  }: {
    id?: string;
    gridIndex?: number;
    maxWidth?: number;
    esiPath?: string;
  },
  creditBannerDetails: CreditBannerDetails,
): DisruptorBlockDetails => {
  const countFrom =
    creditBannerDetails.indices[creditBannerDetails.indices.length - 1] || 0;

  return {
    id,
    gridIndex: gridIndex + countFrom,
    maxWidth,
    esiPath,
  };
};

const shouldDisplayDisruptorBlock = (
  ind: number,
  disruptorBlockDetails: DisruptorBlockDetails,
  productCount: number,
) => {
  if (!disruptorBlockDetails.id) {
    return false;
  }

  // this is the disruptor block index
  if (disruptorBlockDetails?.gridIndex === ind) {
    return true;
  }

  // this is last product & disruptor block index is after last product
  if (
    ind === productCount - 1 &&
    disruptorBlockDetails?.gridIndex >= productCount
  ) {
    return true;
  }

  return false;
};

type CreditBannerDetails = {
  type: CreditBannerType;
  indices: number[];
};

const getCreditBannerDetails = (
  products: Product[],
  chunkSize: number,
): CreditBannerDetails => {
  const firstIfcIndex = products.findIndex(
    (p) => p.credit?.illustration?.financialProductType === "IFC",
  );
  const firstIbcIndex = products.findIndex(
    (p) => p.credit?.illustration?.financialProductType === "IBC",
  );

  if (firstIfcIndex === -1 && firstIbcIndex === -1) {
    return { type: null, indices: [] };
  }
  if (firstIbcIndex === -1) {
    return { type: "IFC", indices: [firstIfcIndex] };
  }
  if (firstIfcIndex === -1) {
    return { type: "IBC", indices: [firstIbcIndex] };
  }
  if (
    Math.floor(firstIfcIndex / chunkSize) ===
    Math.floor(firstIbcIndex / chunkSize)
  ) {
    return {
      type: "IFC-IBC",
      indices: [Math.min(firstIfcIndex, firstIbcIndex)],
    };
  }
  return { type: "IFC-IBC", indices: [firstIbcIndex, firstIfcIndex].sort() };
};
